import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from 'src/services/redux/app/store';
import {
  SHORTCUT_CATALOG,
  SHORTCUT_CATALOG_MAP,
  SHORTCUT_GROUP_ORDER,
  ShortcutCatalogItem,
  ShortcutGroup,
  hasShortcutPermission,
} from './shortcut-catalog';
import { useSaveUserShortcuts, useUserShortcutsQuery } from './shortcuts.service';

const CACHE_PREFIX = 'user-shortcuts';
const SAVE_DEBOUNCE_MS = 600;

function cacheKey(userId?: number | string) {
  return `${CACHE_PREFIX}-${userId || 'default'}`;
}

function loadCache(userId?: number | string): string[] {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function saveCache(keys: string[], userId?: number | string) {
  try {
    localStorage.setItem(cacheKey(userId), JSON.stringify(keys));
  } catch {
    /* yoksay */
  }
}

export interface AvailableGroup {
  group: ShortcutGroup;
  items: Array<ShortcutCatalogItem & { pinned: boolean }>;
}

export function useUserShortcuts() {
  const { user } = useAppSelector((state) => state.auth);
  const authKeys = user?.authorization_keys as string[] | undefined;

  // Anahtarların sıralı listesi (UI için doğruluk kaynağı). İlk boyamada cache'ten anında gelir.
  const [keys, setKeys] = useState<string[]>(() => loadCache(user?.id));

  const { data: serverKeys, isSuccess } = useUserShortcutsQuery();
  const saveMutation = useSaveUserShortcuts();

  const hydratedRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Kullanıcı değişirse (ör. impersonation) yeniden hidrasyon
  useEffect(() => {
    hydratedRef.current = false;
    setKeys(loadCache(user?.id));
  }, [user?.id]);

  // Sunucudan gelen veriyi ilk başarılı yüklemede benimse (cihazlar arası senkron)
  useEffect(() => {
    if (isSuccess && !hydratedRef.current && Array.isArray(serverKeys)) {
      hydratedRef.current = true;
      setKeys(serverKeys);
      saveCache(serverKeys, user?.id);
    }
  }, [isSuccess, serverKeys, user?.id]);

  // Debounce'lu kaydetme
  const scheduleSave = useCallback(
    (nextKeys: string[]) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        saveMutation.mutate(nextKeys);
      }, SAVE_DEBOUNCE_MS);
    },
    [saveMutation],
  );

  // Optimistic güncelleme + cache + debounce'lu backend kaydı
  const persist = useCallback(
    (nextKeys: string[]) => {
      setKeys(nextKeys);
      saveCache(nextKeys, user?.id);
      scheduleSave(nextKeys);
    },
    [scheduleSave, user?.id],
  );

  // Görünür (izinli + katalogda mevcut) kısayollar — çubukta bu sırayla render edilir
  const shortcuts = useMemo(() => {
    return keys
      .map((k) => SHORTCUT_CATALOG_MAP[k])
      .filter((item): item is ShortcutCatalogItem => !!item && hasShortcutPermission(authKeys, item.permission));
  }, [keys, authKeys]);

  const visibleKeySet = useMemo(() => new Set(shortcuts.map((s) => s.key)), [shortcuts]);

  const isPinned = useCallback((key: string) => keys.includes(key), [keys]);

  const add = useCallback(
    (key: string) => {
      if (keys.includes(key)) return;
      if (!SHORTCUT_CATALOG_MAP[key]) return;
      persist([...keys, key]);
    },
    [keys, persist],
  );

  const remove = useCallback(
    (key: string) => {
      if (!keys.includes(key)) return;
      persist(keys.filter((k) => k !== key));
    },
    [keys, persist],
  );

  const toggle = useCallback(
    (key: string) => {
      if (keys.includes(key)) remove(key);
      else add(key);
    },
    [keys, add, remove],
  );

  // Yalnızca görünür kısayolları yeniden sıralar; gizli (izinsiz) anahtarlar sona korunur
  const reorder = useCallback(
    (nextVisibleKeys: string[]) => {
      const hidden = keys.filter((k) => !visibleKeySet.has(k));
      persist([...nextVisibleKeys, ...hidden]);
    },
    [keys, visibleKeySet, persist],
  );

  // Seçici için izinli katalog, gruplu
  const availableByGroup = useMemo<AvailableGroup[]>(() => {
    const groups: AvailableGroup[] = [];
    for (const group of SHORTCUT_GROUP_ORDER) {
      const items = SHORTCUT_CATALOG.filter((item) => item.group === group && hasShortcutPermission(authKeys, item.permission)).map((item) => ({
        ...item,
        pinned: keys.includes(item.key),
      }));
      if (items.length) groups.push({ group, items });
    }
    return groups;
  }, [authKeys, keys]);

  return { shortcuts, availableByGroup, isPinned, add, remove, toggle, reorder };
}
